import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  eventLabels,
  EVENTS,
  FIRST_MESSAGE,
  FIRST_REPLY,
  ingestRuns,
  initialPipeline,
  initialRecall,
  initialRerank,
  initialRows,
  memoryLabels,
  MEMORIES,
  memoryRuns,
  missText,
  NEW_QUESTION,
  PEOPLE,
  personLabels,
  PIPELINE_REFINEMENT,
  pipelineSummary,
  pipelineTransition,
  questionFound,
  questionIds,
  RECALL_QUESTION,
  RECALL_REFINEMENT,
  recalled,
  recallSummary,
  recallText,
  recallTransition,
  RERANK_REFINEMENT,
  rerankSummary,
  rerankTransition,
  ROWS_REFINEMENT,
  rowsSummary,
  rowsTransition,
  searchLabels,
  SEARCHES,
  searchRuns,
  staleCount,
  staleFlag,
  storedRows,
  storeLabels,
  STORES,
  systemText,
  toEvent,
  tokensPerQuestionText,
  tokensText,
  toMemory,
  toPerson,
  toSearch,
  toStore,
  yesNo,
  type PipelineAction,
  type RecallAction,
  type RerankAction,
  type RowsAction,
} from './memory-models';

import './relationship-labs.css';
import './memory-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';
const SIMULATION = 'This graphic simulates recall over four stored rows.';
const INGEST = 'This graphic replays the ingest of the twelve documents.';

export function MemoryRowsLab() {
  const [state, setState] = useState(initialRows);
  const act = (action: RowsAction) => setState((current) => rowsTransition(current, action));
  const run = memoryRuns[state.memory];
  const summary = rowsSummary(state);
  return (
    <Lab
      title="What does the assistant remember in the next conversation?"
      eyebrow="Memory · rows the assistant writes"
      className="rel-lab memory-lab"
    >
      <Predict>
        What does the assistant remember in the next conversation? Choose a Memory, then press Ask
        in a new conversation.
      </Predict>
      <div className="cb-controls">
        <label>
          Memory
          <ModelSelect
            aria-label="Memory"
            value={state.memory}
            onValueChange={(value) => act({ type: 'memory', value: toMemory(value) })}
            options={MEMORIES.map((id) => ({ value: id, label: memoryLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask in a new conversation
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The first conversation">
        <pre aria-label="The first conversation">{`Maya: ${FIRST_MESSAGE}\nAssistant: ${FIRST_REPLY}`}</pre>
      </Panel>
      <Panel label="The new conversation's system message">
        <pre aria-label="The new conversation's system message">{systemText(run)}</pre>
      </Panel>
      <Panel label="The new conversation">
        <pre aria-label="The new conversation">
          {state.asked ? `Maya: ${NEW_QUESTION}\nAssistant: ${run.reply}` : `Maya: ${NEW_QUESTION}`}
        </pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Ids first', yesNo(run.idsFirst)],
            ['Called her M', yesNo(run.calledM)],
            ['Memory rows', String(run.rows)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.asked ? <p className="rel-caption">{ROWS_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function MemoryRecallLab() {
  const [state, setState] = useState(initialRecall);
  const act = (action: RecallAction) => setState((current) => recallTransition(current, action));
  const stored = storedRows(state.store);
  const found = recalled(state.person, state.store);
  const summary = recallSummary(state);
  return (
    <Lab
      title="Which memories reach the prompt?"
      eyebrow="Memory · recall is retrieval"
      className="rel-lab memory-lab"
    >
      <Predict>
        Which memories reach the prompt? Choose a Person and the Memories stored, then press Recall.
      </Predict>
      <div className="cb-controls">
        <label>
          Person
          <ModelSelect
            aria-label="Person"
            value={state.person}
            onValueChange={(value) => act({ type: 'person', value: toPerson(value) })}
            options={PEOPLE.map((id) => ({ value: id, label: personLabels[id] }))}
          />
        </label>
        <label>
          Memories stored
          <ModelSelect
            aria-label="Memories stored"
            value={state.store}
            onValueChange={(value) => act({ type: 'store', value: toStore(value) })}
            options={STORES.map((id) => ({ value: id, label: storeLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Recall
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The turn">
        <pre aria-label="The turn">{`${personLabels[state.person]}: ${RECALL_QUESTION}`}</pre>
      </Panel>
      <Table
        label="Memories stored"
        columns={['Person', 'Text', 'Created']}
        template="minmax(0, 0.6fr) minmax(0, 2.4fr) minmax(0, 1fr)"
        rows={stored.map((entry) => [personLabels[entry.person], entry.text, entry.date])}
      />
      <Table
        label="Recalled for this turn"
        columns={['Text', 'Created', 'Flag']}
        template="minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1.4fr)"
        rows={
          state.run
            ? found.map((entry) => [entry.text, entry.date, staleFlag(entry, state.store)])
            : []
        }
      />
      {state.run ? (
        <Facts
          values={[
            ['Rows stored', String(stored.length)],
            ['Recalled', String(found.length)],
            ['Flagged stale', String(staleCount(state.person, state.store))],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{RECALL_REFINEMENT}</p> : null}
      <p className="rel-caption">{SIMULATION}</p>
    </Lab>
  );
}

export function KbPipelineLab() {
  const [state, setState] = useState(initialPipeline);
  const act = (action: PipelineAction) =>
    setState((current) => pipelineTransition(current, action));
  const run = ingestRuns[state.event];
  const summary = pipelineSummary(state);
  return (
    <Lab
      title="What happens to the index when a document changes?"
      eyebrow="Knowledge base · a pipeline, not a folder"
      className="rel-lab memory-lab"
    >
      <Predict>
        What happens to the index when a document changes? Choose an Event, then press Run the
        ingest.
      </Predict>
      <div className="cb-controls">
        <label>
          Event
          <ModelSelect
            aria-label="Event"
            value={state.event}
            onValueChange={(value) => act({ type: 'event', value: toEvent(value) })}
            options={EVENTS.map((id) => ({ value: id, label: eventLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run the ingest
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The ingest"
        columns={['Documents', 'Pieces', 'What the ingest did']}
        template="minmax(0, 1.6fr) minmax(0, 0.5fr) minmax(0, 1.6fr)"
        rows={state.run ? run.rows.map((entry) => [entry.documents, entry.pieces, entry.did]) : []}
      />
      <Panel label="The ingest printed">
        <pre aria-label="The ingest printed">{state.run ? run.output : '(not run yet)'}</pre>
      </Panel>
      {state.run ? (
        <Facts
          values={[
            ['Pieces stored', String(run.stored)],
            ['Pieces skipped', String(run.skipped)],
            ['Pieces left behind', String(run.leftBehind)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{PIPELINE_REFINEMENT}</p> : null}
      <p className="rel-caption">{INGEST}</p>
    </Lab>
  );
}

export function KbRerankLab() {
  const [state, setState] = useState(initialRerank);
  const act = (action: RerankAction) => setState((current) => rerankTransition(current, action));
  const run = searchRuns[state.search];
  const summary = rerankSummary(state);
  return (
    <Lab
      title="What does each search step find?"
      eyebrow="Knowledge base · better search in two steps"
      className="rel-lab memory-lab"
    >
      <Predict>
        What does each search step find? Choose a Search, then press Run the seventeen questions.
      </Predict>
      <div className="cb-controls">
        <label>
          Search
          <ModelSelect
            aria-label="Search"
            value={state.search}
            onValueChange={(value) => act({ type: 'search', value: toSearch(value) })}
            options={SEARCHES.map((id) => ({ value: id, label: searchLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run the seventeen questions
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The seventeen questions">
        {state.run ? (
          <ol
            className="memory-recall-grid"
            role="img"
            aria-label={`Recall@5 ${recallText(run)}, miss ${run.miss ?? 'none'}`}
          >
            {questionIds.map((id) => (
              <li key={id} data-found={questionFound(run, id)}>
                {id}
              </li>
            ))}
          </ol>
        ) : (
          <pre aria-label="The seventeen questions">(not run yet)</pre>
        )}
      </Panel>
      <Panel label="The missed question">
        <pre aria-label="The missed question">
          {state.run
            ? `${missText(run)}${run.queryUsed === null ? '' : `\nthis step: ${run.queryUsed}`}`
            : '(not run yet)'}
        </pre>
      </Panel>
      {state.run ? (
        <Facts
          values={[
            ['Recall@5', recallText(run)],
            ['Tokens for 17', tokensText(run.tokens)],
            ['Tokens per question', tokensPerQuestionText(run)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{RERANK_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
